import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import { toast } from 'react-toastify';
import { RegisteredStudent } from '../api/families';

interface ParentArrivalDialogProps {
  open: boolean;
  onClose: () => void;
  students: RegisteredStudent[];
  onSubmit: (studentIds: number[], pickupType: 'car' | 'foot') => Promise<void>;
}

export default function ParentArrivalDialog({
  open,
  onClose,
  students,
  onSubmit,
}: ParentArrivalDialogProps) {
  // Select all students by default
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>(() =>
    students.map((s) => s.id)
  );
  const [pickupType, setPickupType] = useState<'car' | 'foot'>('car');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleToggleStudent = (studentId: number) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    setSelectedStudentIds(students.map((s) => s.id));
  };

  const handleDeselectAll = () => {
    setSelectedStudentIds([]);
  };

  const handleSubmit = async () => {
    if (selectedStudentIds.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(selectedStudentIds, pickupType);
      toast.success(
        `Checked in ${selectedStudentIds.length} student(s) for ${pickupType === 'car' ? 'car' : 'walker'} pickup`
      );
      onClose();
      // Reset selections for next time
      setSelectedStudentIds(students.map((s) => s.id));
      setPickupType('car');
    } catch (error) {
      console.error('[ParentArrivalDialog] Submit error:', error);
      toast.error('Failed to notify arrival. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>I'm Here for Pickup</DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Let staff know you've arrived! Select which students you're picking up and
            how you're arriving.
          </Alert>
        </Box>

        {/* Student Selection */}
        <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <FormLabel component="legend">Select Students</FormLabel>
            <Box>
              <Button size="small" onClick={handleSelectAll}>
                Select All
              </Button>
              <Button size="small" onClick={handleDeselectAll}>
                Deselect All
              </Button>
            </Box>
          </Box>

          <FormGroup>
            {students.map((student) => (
              <FormControlLabel
                key={student.id}
                control={
                  <Checkbox
                    checked={selectedStudentIds.includes(student.id)}
                    onChange={() => handleToggleStudent(student.id)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      {student.first_name} {student.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Grade {student.grade}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </FormGroup>
        </FormControl>

        {/* Pickup Type Selection */}
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">How are you arriving?</FormLabel>
          <RadioGroup
            value={pickupType}
            onChange={(e) => setPickupType(e.target.value as 'car' | 'foot')}
          >
            <FormControlLabel
              value="car"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DirectionsCarIcon />
                  <Typography>Car Pickup</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="foot"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DirectionsWalkIcon />
                  <Typography>Walking/Foot</Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={isSubmitting}
          variant="pickup"
          disabled={selectedStudentIds.length === 0}
        >
          Notify Staff ({selectedStudentIds.length} student
          {selectedStudentIds.length !== 1 ? 's' : ''})
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
